import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

const badgeVariants = cva(
  cn(
    /* sorted */
    "inline-flex items-center justify-center gap-1 shrink-0 overflow-hidden",
    "w-fit",
    "font-normal text-sm leading-5 whitespace-nowrap",
    "transition-[color,box-shadow]",
    "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-0.5",
  ),
  {
    variants: {
      kind: {
        solid: "text-text-light",
        tonal: "",
        outline: "border border-border-dark-low",
        ghost: "",
      },
      color: {
        purple: "",
        black: "",
        red: "",
        green: "",
        orange: "",
        yellow: "",
        lime: "",
        blue: "",
        indigo: "",
        fuchsia: "",
        pink: "",
        rose: "",
        amber: "",
        cyan: "",
      },
      shape: {
        fill: "rounded-full",
        rounded: "rounded-md",
        circle: "rounded-full",
      },
      size: {
        xs: "px-1.5 py-0 has-[svg]:pl-0.5 has-[svg]:pr-1.5",
        sm: "px-2 py-0.5 has-[svg]:pl-1 has-[svg]:pr-2",
        md: "px-2 py-1 has-[svg]:pl-1.5 has-[svg]:pr-2",
        lg: "px-3 py-1.5 has-[svg]:pl-2 has-[svg]:pr-3",
      },
    },
    compoundVariants: [
      // === SOLID ===
      {
        kind: "solid",
        color: "purple",
        class: "bg-bg-primary-max",
      },
      {
        kind: "solid",
        color: "black",
        class: "bg-bg-dark-max",
      },
      {
        kind: "solid",
        color: "red",
        class: "bg-bg-negative-max",
      },
      {
        kind: "solid",
        color: "green",
        class: "bg-bg-success-max",
      },
      {
        kind: "solid",
        color: "orange",
        class: "bg-bg-warning-max",
      },
      {
        kind: "solid",
        color: "purple",
        class: "bg-bg-primary-max",
      },
      {
        kind: "solid",
        color: "black",
        class: "bg-bg-dark-max",
      },
      {
        kind: "solid",
        color: "red",
        class: "bg-bg-negative-max",
      },
      {
        kind: "solid",
        color: "green",
        class: "bg-bg-success-max",
      },
      {
        kind: "solid",
        color: "orange",
        class: "bg-bg-warning-max",
      },
      {
        kind: "solid",
        color: "yellow",
        class: "bg-bg-yellow-max",
      },
      {
        kind: "solid",
        color: "lime",
        class: "bg-bg-lime-max",
      },
      {
        kind: "solid",
        color: "blue",
        class: "bg-bg-blue-max",
      },
      {
        kind: "solid",
        color: "indigo",
        class: "bg-bg-indigo-max",
      },
      {
        kind: "solid",
        color: "fuchsia",
        class: "bg-bg-fuchsia-max",
      },
      {
        kind: "solid",
        color: "pink",
        class: "bg-bg-pink-max",
      },
      {
        kind: "solid",
        color: "rose",
        class: "bg-bg-rose-max",
      },
      {
        kind: "solid",
        color: "amber",
        class: "bg-bg-amber-max",
      },
      {
        kind: "solid",
        color: "cyan",
        class: "bg-bg-cyan-max",
      },

      // === TONAL ===
      {
        kind: "tonal",
        color: "purple",
        class: "text-text-primary bg-bg-primary-min",
      },
      {
        kind: "tonal",
        color: "black",
        class: "text-text-dark-max bg-bg-dark-min",
      },
      {
        kind: "tonal",
        color: "red",
        class: "text-text-negative bg-bg-negative-min",
      },
      {
        kind: "tonal",
        color: "green",
        class: "text-text-success bg-bg-success-min",
      },
      {
        kind: "tonal",
        color: "orange",
        class: "text-text-warning bg-bg-warning-min",
      },
      {
        kind: "tonal",
        color: "purple",
        class: "text-text-primary bg-bg-primary-min",
      },
      {
        kind: "tonal",
        color: "black",
        class: "text-text-dark-max bg-bg-dark-min",
      },
      {
        kind: "tonal",
        color: "red",
        class: "text-text-negative bg-bg-negative-min",
      },
      {
        kind: "tonal",
        color: "green",
        class: "text-text-success bg-bg-success-min",
      },
      {
        kind: "tonal",
        color: "orange",
        class: "text-text-warning bg-bg-warning-min",
      },
      {
        kind: "tonal",
        color: "yellow",
        class: "text-text-yellow bg-bg-yellow-min",
      },
      {
        kind: "tonal",
        color: "lime",
        class: "text-text-lime bg-bg-lime-min",
      },
      {
        kind: "tonal",
        color: "blue",
        class: "text-text-blue bg-bg-blue-min",
      },
      {
        kind: "tonal",
        color: "indigo",
        class: "text-text-indigo bg-bg-indigo-min",
      },
      {
        kind: "tonal",
        color: "fuchsia",
        class: "text-text-fuchsia bg-bg-fuchsia-min",
      },
      {
        kind: "tonal",
        color: "pink",
        class: "text-text-pink bg-bg-pink-min",
      },
      {
        kind: "tonal",
        color: "rose",
        class: "text-text-rose bg-bg-rose-min",
      },
      {
        kind: "tonal",
        color: "amber",
        class: "text-text-amber bg-bg-amber-min",
      },
      {
        kind: "tonal",
        color: "cyan",
        class: "text-text-cyan bg-bg-cyan-min",
      },

      // === OUTLINE ===
      {
        kind: "outline",
        color: "purple",
        class: "text-text-primary border-border-primary-min",
      },
      {
        kind: "outline",
        color: "black",
        class: "text-text-dark-max border-border-dark-low",
      },
      {
        kind: "outline",
        color: "red",
        class: "text-text-negative border-border-negative-min",
      },
      {
        kind: "outline",
        color: "green",
        class: "text-text-success border-border-success-min",
      },
      {
        kind: "outline",
        color: "orange",
        class: "text-text-warning border-border-warning-min",
      },
      {
        kind: "outline",
        color: "yellow",
        class: "text-text-yellow border-border-yellow-min",
      },
      {
        kind: "outline",
        color: "lime",
        class: "text-text-lime border-border-lime-min",
      },
      {
        kind: "outline",
        color: "blue",
        class: "text-text-blue border-border-blue-min",
      },
      {
        kind: "outline",
        color: "indigo",
        class: "text-text-indigo border-border-indigo-min",
      },
      {
        kind: "outline",
        color: "fuchsia",
        class: "text-text-fuchsia border-border-fuchsia-min",
      },
      {
        kind: "outline",
        color: "pink",
        class: "text-text-pink border-border-pink-min",
      },
      {
        kind: "outline",
        color: "rose",
        class: "text-text-rose border-border-rose-min",
      },
      {
        kind: "outline",
        color: "amber",
        class: "text-text-amber border-border-amber-min",
      },
      {
        kind: "outline",
        color: "cyan",
        class: "text-text-cyan border-border-cyan-min",
      },

      // === GHOST ===
      {
        kind: "ghost",
        color: "purple",
        class: "text-text-primary",
      },
      {
        kind: "ghost",
        color: "black",
        class: "text-text-dark-max",
      },
      {
        kind: "ghost",
        color: "red",
        class: "text-text-negative",
      },
      {
        kind: "ghost",
        color: "green",
        class: "text-text-success",
      },
      {
        kind: "ghost",
        color: "orange",
        class: "text-text-warning",
      },
      {
        kind: "ghost",
        color: "purple",
        class: "text-text-primary",
      },
      {
        kind: "ghost",
        color: "black",
        class: "text-text-dark-max",
      },
      {
        kind: "ghost",
        color: "red",
        class: "text-text-negative",
      },
      {
        kind: "ghost",
        color: "green",
        class: "text-text-success",
      },
      {
        kind: "ghost",
        color: "orange",
        class: "text-text-warning",
      },
      {
        kind: "ghost",
        color: "yellow",
        class: "text-text-yellow",
      },
      {
        kind: "ghost",
        color: "lime",
        class: "text-text-lime",
      },
      {
        kind: "ghost",
        color: "blue",
        class: "text-text-blue",
      },
      {
        kind: "ghost",
        color: "indigo",
        class: "text-text-indigo",
      },
      {
        kind: "ghost",
        color: "fuchsia",
        class: "text-text-fuchsia",
      },
      {
        kind: "ghost",
        color: "pink",
        class: "text-text-pink",
      },
      {
        kind: "ghost",
        color: "rose",
        class: "text-text-rose",
      },
      {
        kind: "ghost",
        color: "amber",
        class: "text-text-amber",
      },
      {
        kind: "ghost",
        color: "cyan",
        class: "text-text-cyan",
      },

      // === SHAPE ===
      {
        size: "xs",
        shape: "rounded",
        class: "rounded-sm",
      },
      {
        size: "sm",
        shape: "rounded",
        class: "rounded-sm",
      },
      {
        size: "md",
        shape: "rounded",
        class: "rounded-md",
      },
      {
        size: "lg",
        shape: "rounded",
        class: "rounded-lg",
      },
    ],
    defaultVariants: {
      kind: "tonal",
      color: "black",
      shape: "fill",
      size: "md",
    },
  },
);

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
  };

function Badge({
  className,
  kind,
  color,
  shape,
  size,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ kind, color, shape, size }), className)}
      {...props}
    />
  );
}

export type { BadgeProps };
export { Badge, badgeVariants };
